import type { AuthState, ConnectorStatus, PaneQuery } from '../../shared/contracts';
import { parsePaneData } from '../../shared/pane-schema';
import { z } from 'zod';

const threadSchema = z.object({
  ticketId: z.number().int(),
  truncated: z.boolean(),
  messages: z.array(
    z.object({
      id: z.number().int(),
      at: z.string().nullable(),
      author: z.string(),
      body: z.string(),
      internal: z.boolean(),
      ours: z.boolean(),
    }),
  ),
});
export type TicketThreadData = z.infer<typeof threadSchema>;

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Request failed: ${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

/** Builds the `?period=…&owner=…` query string for a pane request from a typed PaneQuery. */
export function paneQueryString(query?: PaneQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  if (query.period) {
    if (typeof query.period === 'string') {
      params.set('period', query.period);
    } else {
      params.set('from', query.period.from);
      params.set('to', query.period.to);
    }
  }
  if (query.owner) params.set('owner', query.owner);
  if (query.customer) params.set('customer', query.customer);
  if (query.listId) params.set('listId', query.listId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  authState: () => getJson<AuthState>('/auth/me'),
  health: () => getJson<{ status: string; connectors?: ConnectorStatus[] }>('/api/health'),
  // Pane payloads drive charts/aggregates — validate instead of trusting the cast, so a
  // malformed response surfaces as the pane's error state, never NaN/blank rows.
  pane: async (id: string, query?: PaneQuery) =>
    parsePaneData(await getJson<unknown>(`/api/panes/${id}${paneQueryString(query)}`)),
  ticketThread: async (ticketId: number) =>
    threadSchema.parse(await getJson<unknown>(`/api/tickets/${ticketId}/thread`)),
};
